import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableSlotGroup,
  RichTextContent,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

// ─── Slot group definitions ───────────────────────────────────────────────────

const photoDef: SlotGroupDefinition = {
  label: 'Pet Photo',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      // Stock cat-and-dog photo by default; there is no pet-specific property
      // image field, so this renders until an editor picks a custom image.
      defaultValue:
        'https://images.unsplash.com/photo-1450778869180-41d0601e046e?auto=format&fit=crop&w=1200&q=80',
      suggestedFields: ['property_featured_image'],
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'A cat and dog resting together',
    },
  },
};

const ctaLinkDef: SlotGroupDefinition = {
  label: 'Pet Screening CTA',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: 'https://americanlandmark.petscreening.com/',
    },
    label: {
      label: 'Button Text',
      type: 'text',
      defaultValue: 'Begin Pet Screening',
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const learnMoreCtaDef: SlotGroupDefinition = {
  label: 'Learn More CTA',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/pet-policy',
    },
    label: { label: 'Button Text', type: 'text', defaultValue: 'Learn More' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// ─── Default body content (matches the reference design) ─────────────────────

const DEFAULT_BODY_HTML =
  '<p><a href="/pet-policy">Click Here</a> to learn about our Pet Policy.</p>' +
  '<p>Based on the Fido results from Pet Screening, the pet fees range from $300-400 per pet and the pet rent will range from $25-45 per pet.</p>' +
  '<p>All applicants, even with no pet households or assistance animals, need to complete our pet screening. Screening must be completed to continue your application. Start your screening today.</p>';

// ─── Component ────────────────────────────────────────────────────────────────

export function PetPolicyCallout() {
  const photo = useSlotGroup('petpolicycallout.photo', photoDef);
  const ctaLink = useSlotGroup('petpolicycallout.cta_link', ctaLinkDef);
  const learnMoreCta = useSlotGroup(
    'petpolicycallout.learn_more_cta',
    learnMoreCtaDef,
  );

  return (
    <section className="bg-white py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-12 lg:gap-16 items-center">

          {/* Left: photo with rounded corners */}
          <EditableSlotGroup
            groupPath="petpolicycallout.photo"
            definition={photoDef}
            className="block"
          >
            {photo.image ? (
              <VibesitesImage
                url={photo.image}
                alt={photo.alt || ''}
                className="w-full rounded-lg object-cover shadow-sm"
                style={{ aspectRatio: '4/5' }}
              />
            ) : (
              <div
                className="w-full rounded-lg bg-[var(--vibe-color-surface)]"
                style={{ aspectRatio: '4/5' }}
              />
            )}
          </EditableSlotGroup>

          {/* Right: text content + CTA */}
          <div className="md:pl-4 lg:pl-8">
            <RichTextContent
              path="petpolicycallout.heading"
              label="Heading"
              defaultValue="<p>For Our Furry Friends</p>"
              as="h2"
              className="font-heading text-4xl sm:text-5xl md:text-6xl font-normal text-[var(--vibe-color-text)] mb-8 leading-[1.1]"
            />

            <RichTextContent
              path="petpolicycallout.body"
              label="Body Content"
              defaultValue={DEFAULT_BODY_HTML}
              as="div"
              className="pet-policy-callout-body font-body text-base text-[var(--vibe-color-text)] leading-relaxed mb-8"
            />

            <div className="flex flex-col sm:flex-row flex-wrap gap-4">
              {/* Primary: pet screening (the required action) */}
              <EditableSlotGroup
                groupPath="petpolicycallout.cta_link"
                definition={ctaLinkDef}
                className="block"
              >
                <a
                  href={ctaLink.url || 'https://americanlandmark.petscreening.com/'}
                  target={ctaLink._target || '_blank'}
                  rel={ctaLink._target === '_blank' ? 'noopener noreferrer' : undefined}
                  className="pet-policy-cta-btn pet-policy-cta-btn--primary inline-flex items-center justify-center px-8 py-3.5 text-sm font-semibold tracking-wide rounded-sm transition-all duration-200"
                >
                  {ctaLink.label || 'Begin Pet Screening'}
                </a>
              </EditableSlotGroup>

              {/* Secondary: learn more about the pet policy */}
              <EditableSlotGroup
                groupPath="petpolicycallout.learn_more_cta"
                definition={learnMoreCtaDef}
                className="block"
              >
                <a
                  href={learnMoreCta.url || '/pet-policy'}
                  target={learnMoreCta._target || '_self'}
                  rel={
                    learnMoreCta._target === '_blank'
                      ? 'noopener noreferrer'
                      : undefined
                  }
                  className="pet-policy-cta-btn pet-policy-cta-btn--secondary inline-flex items-center justify-center px-8 py-3.5 text-sm font-semibold tracking-wide rounded-sm transition-all duration-200"
                >
                  {learnMoreCta.label || 'Learn More'}
                </a>
              </EditableSlotGroup>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .pet-policy-callout-body p {
          margin-bottom: 1.25rem;
        }
        .pet-policy-callout-body p:last-child {
          margin-bottom: 0;
        }
        .pet-policy-callout-body a {
          text-decoration: underline;
          text-underline-offset: 2px;
          font-weight: 600;
          color: var(--vibe-color-text);
          transition: color 0.2s ease;
        }
        .pet-policy-callout-body a:hover {
          color: var(--vibe-color-secondary);
        }

        /* CTA buttons — brand-aware, one per action */
        .pet-policy-cta-btn:hover {
          transform: translateY(-1px);
        }

        /* Primary: pet screening (required next step) */
        .pet-policy-cta-btn--primary {
          background-color: var(--vibe-color-primary);
          color: var(--vibe-color-primary-foreground, #fff);
        }
        .pet-policy-cta-btn--primary:hover {
          background-color: color-mix(in srgb, var(--vibe-color-primary) 85%, black);
        }

        /* Secondary: learn more about the pet policy */
        .pet-policy-cta-btn--secondary {
          background-color: var(--vibe-color-secondary);
          color: var(--vibe-color-secondary-foreground, #fff);
        }
        .pet-policy-cta-btn--secondary:hover {
          background-color: color-mix(in srgb, var(--vibe-color-secondary) 85%, black);
        }
      `}</style>
    </section>
  );
}

// ─── Content Schema ───────────────────────────────────────────────────────────

export const contentSchema: ComponentContentSchema = {
  slot: 'PetPolicyCallout',
  displayName: 'Pet Policy Callout',
  tokens: {
    'petpolicycallout.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: '<p>For Our Furry Friends</p>',
    },
    'petpolicycallout.body': {
      label: 'Body Content',
      type: 'richtext',
      defaultValue: DEFAULT_BODY_HTML,
    },
  },
  slotGroups: {
    'petpolicycallout.photo': photoDef,
    'petpolicycallout.cta_link': ctaLinkDef,
    'petpolicycallout.learn_more_cta': learnMoreCtaDef,
  },
};

(PetPolicyCallout as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default PetPolicyCallout;
